import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/core/services/data.service';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { Utils } from 'src/app/core/services/utils';
import { AuthData } from 'src/app/models/auth/auth-data.model';
import { AuthenticationUserCompany } from 'src/app/models/auth/authentication-response.model';
import { CacheService } from 'src/app/services/cache.service';
import { ScopeType } from 'src/app/modules/system-level/components/integrations/integrations.component';

interface ExternalAuthRedeemResponse {
  status?: string;
  accessToken?: string;
  refreshToken?: string;
  scope?: string;
  userName?: string;
  userCompanyGuid?: string;
  companyGuid?: string;
  availableCompanies?: AuthenticationUserCompany[];
}

@Component({
    selector: 'ext-auth-callback',
    templateUrl: 'ext-auth-callback.component.html',
    standalone: false
})
export class ExternalAuthCallbackComponent implements OnInit {

	private _activatedRouteQueryParamsSubscription: Subscription;
	private _activatedRouteParamsSubscription: Subscription;

	private _token: string;
	private _code: string;

	private _queryParams: string = "";

	private _returnUrl: string = "";

	constructor(
		private readonly _activatedRoute: ActivatedRoute,
		private readonly _dataService: DataService,
		private readonly _environment: EnvironmentService,
		private readonly _localStorageService: LocalStorageService,
		private readonly _cacheService: CacheService
	) { }

	ngOnInit() {
		this._activatedRouteQueryParamsSubscription = this._activatedRoute.queryParams.subscribe((queryParams) => {
			this._activatedRouteParamsSubscription = this._activatedRoute.params.subscribe((routeParams) => {
        let scope = "Admin";
				Object.keys(queryParams).forEach(key => {
          if (key === 'token') {
            this._token = queryParams[key];
          } else if (key === 'r_url') {
            this._returnUrl = queryParams[key];
          } else if (key === 'scope') {
            scope = queryParams[key];
          } else if (key === 'code') {
            this._code = queryParams[key];
          } else {
            this._queryParams += `${key}=${queryParams[key]}&`;
          }
				});
        if (this._code) {
          history.replaceState({}, '', window.location.pathname);
        }

        const redirect = () => {
          let urlToRedirectTo = this._returnUrl;
          if (!urlToRedirectTo) {
            if (scope === 'TPO') {
              urlToRedirectTo = '/tpo/dashboard';
            } else {
              urlToRedirectTo = '/admin/dashboardv2';
            }
          }

          setTimeout(() => {
            const isAbsoluteUrl = urlToRedirectTo.startsWith('http://') || urlToRedirectTo.startsWith('https://');
            const urlToGoTo = isAbsoluteUrl
              ? urlToRedirectTo
              : `${window.location.protocol}//${window.location.host}${urlToRedirectTo}?${this._queryParams}`;
            window.location.href = urlToGoTo;
          }, 2000);
        };

        if (!this._code) {
          this.persistAuthData();
          redirect();
          return;
        }

        const url = this._environment.apiInfo.apiBaseUrl + 'api/auth/saml-sso/redeem';
        this._dataService.postWithoutAuth(url, { code: this._code }, false).subscribe({
          next: (response: ExternalAuthRedeemResponse) => {
            if (!this.persistRedeemedAuthData(response)) {
              this.persistAuthData();
            }
            redirect();
          },
          error: () => {
            this.persistAuthData();
            redirect();
          }
        });
			});
		});
	}

	ngOnDestroy(): void {
		this._activatedRouteQueryParamsSubscription?.unsubscribe();
		this._activatedRouteParamsSubscription?.unsubscribe();
	}

	private persistAuthData = () => {
		const jwtPayload = Utils.parseJwt(this._token);

		this._localStorageService.authorizationData = {
			token: this._token,
			userName: jwtPayload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
			userId: jwtPayload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
			scope: 'Borrower',
			companyGuid: jwtPayload.CompanyGuid,
			expiresAt: Utils.getTokenExpireDate(jwtPayload.exp)
		} as unknown as AuthData;
	}

  private persistRedeemedAuthData = (response: ExternalAuthRedeemResponse): boolean => {
    if (response?.status !== 'Success' || !response.accessToken || !response.refreshToken) {
      return false;
    }

    let jwtPayload: any;
    try {
      jwtPayload = Utils.parseJwt(response.accessToken);
    } catch {
      return false;
    }

    if (!jwtPayload || !jwtPayload.exp) {
      return false;
    }

    const userCompanyGuid = response.userCompanyGuid
      || jwtPayload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
      || jwtPayload.sub;
    const companyGuid = response.companyGuid ?? jwtPayload.CompanyGuid;

    if (!userCompanyGuid || !companyGuid) {
      return false;
    }

    const authData: AuthData = {
      companyGuid,
      userCompanyGuid,
      token: response.accessToken,
      role: jwtPayload.AspNetRole,
      userName: response.userName,
      refreshToken: response.refreshToken,
      companies: response.availableCompanies ?? [],
      rememberMe: false,
      expiresAt: Utils.getTokenExpireDate(jwtPayload.exp)
    };

    this._cacheService.setTenantPrefix(authData.userCompanyGuid);
    this._localStorageService.authorizationData = authData;

    return true;
  }
}
